// TestDashboard.vue

<template>
  <div class="page-container">
    <div v-if="isLoading">Memuat dashboard...</div>
    <div v-else class="dashboard-content">
      <header class="dashboard-header">
        <h1>Selamat Datang, {{ participantName }}!</h1>
        <p>Paket Tes: <strong>{{ packageName }}</strong></p>
        <p class="instruction">Silakan kerjakan tes di bawah ini sesuai dengan intruksi pemberi tes.</p>
      </header>

      <div v-if="allTestsCompleted" class="results-banner">
        <h2>🎉 Semua Tes Telah Selesai!</h2>
        <p>Anda telah menyelesaikan semua rangkaian tes.</p>
        
      </div>
      <div class="test-list">
        <div v-for="(test, index) in tests" :key="test.id" class="test-item" :class="test.status">
          <div class="test-info">
            <h2>{{ index + 1 }}. {{ test.name }}</h2>
            <p v-if="test.description">{{ test.description }}</p>
          </div>
          <div class="test-actions">
            <span class="status-badge" :class="test.status">
              {{ formatStatus(test.status) }}
            </span>
            <button 
              @click="startTest(test.id)" 
              :disabled="test.status === 'completed'"
              class="start-button"
            >
              {{ test.status === 'completed' ? 'Selesai' : 'Mulai Tes' }}
            </button>
          </div>
        </div>
      </div>
      
      <!-- Tombol untuk kembali ke menu awal -->
      <footer class="dashboard-footer">
        <button @click="backToHome" class="back-button">Kembali ke Menu Awal</button>
      </footer>
    </div>
    <p v-if="error" class="error-message">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import apiClient from '@/services/api.js';

const router = useRouter();
const isLoading = ref(true);
const participantName = ref('');
const packageName = ref('');
const tests = ref([]);
const error = ref('');

// src/views/TestDashboard.vue -> <script setup>

onMounted(async () => {
  isLoading.value = true;
  error.value = '';
  try {
    // PANGGILAN API YANG BENAR UNTUK DASHBOARD PESERTA
    const response = await apiClient.get('/sessions/status');
    console.log("DATA DARI /api/sessions/status:", response.data);
    const data = response.data;
    participantName.value = data.participant_name;
    packageName.value = data.package_name;
    tests.value = data.tests;
  } catch (err) {
    error.value = "Gagal memuat sesi Anda. Silakan coba masuk kembali.";
    console.error("Dashboard fetch error:", err.response);
    
    // Jika gagal (misal, token tidak valid), hapus sesi dan kembali ke home
    localStorage.removeItem('session_token');
    localStorage.removeItem('test_info');
    localStorage.removeItem('test_end_time');
    router.push({ name: 'Home' });
  } finally {
    isLoading.value = false;
  }
});


// --- TAMBAHKAN COMPUTED PROPERTY INI ---
const allTestsCompleted = computed(() => {
  // Pastikan 'tests.value' tidak kosong dan setiap tes di dalamnya punya status 'completed'
  return tests.value.length > 0 && tests.value.every(test => test.status === 'completed');
});

// --- TAMBAHKAN FUNGSI INI ---
//const viewResults = () => {
//  router.push({ name: 'TestResult' }); // Arahkan ke halaman hasil peserta
//};


const startTest = (testId) => {
  // Logika ini sudah benar, mengarah ke preview
  router.push({ name: 'TestPreview', params: { id: testId } });
};

const formatStatus = (status) => {
  if (status === 'not_started') return 'Belum Dikerjakan';
  if (status === 'in_progress') return 'Sedang Dikerjakan';
  if (status === 'completed') return 'Selesai';
  return status;
};
// --- TAMBAHKAN FUNGSI INI ---
const backToHome = () => {
  if (window.confirm("Apakah Anda yakin ingin kembali ke menu awal? Sesi Anda saat ini akan diakhiri dan Anda harus masuk kembali.")) {
    localStorage.removeItem('session_token');
    localStorage.removeItem('test_info'); // Hapus data sesi lainnya
    localStorage.removeItem('test_end_time');
    router.push({ name: 'Home' });
  }
};

</script>

<style scoped>
.page-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
}
.dashboard-header {
  text-align: center;
  margin-bottom: 40px;
}
.dashboard-header h1 {
  margin-bottom: 5px;
}
.dashboard-header p {
  margin: 5px 0;
  color: #555;
}
.instruction {
  font-style: italic;
}

.test-list {
  display: flex;
  flex-direction: column;
  gap: 20px; /* Jarak antar item tes */
}

.test-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;
}
.test-item:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.test-item.completed {
  background-color: #f8f9fa;
  color: #6c757d;
}

.test-info h2 {
  margin: 0 0 5px 0;
  font-size: 1.5em;
}
.test-info p {
  margin: 0;
  font-size: 0.9em;
  color: #666;
}

.test-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  min-width: 150px; /* Beri lebar minimum agar tidak berantakan */
}

.status-badge {
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
}
.status-badge.not_started { background-color: #e9ecef; color: #495057; }
.status-badge.in_progress { background-color: #fff3cd; color: #856404; }
.status-badge.completed { background-color: #d4edda; color: #155724; }

.start-button {
  padding: 8px 16px;
  border: 1px solid #1abc9c;
  background-color: #1abc9c;
  color: white;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s;
}
.start-button:hover {
  background-color: #16a085;
}
.start-button:disabled {
  background-color: #bdc3c7;
  border-color: #bdc3c7;
  cursor: not-allowed;
}

.dashboard-footer {
  margin-top: 40px;
  text-align: center;
}
.back-button {
  background: none;
  border: 1px solid #777;
  color: #777;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
}
.back-button:hover {
  background-color: #f0f0f0;
}

/* Gaya untuk banner hasil */
.results-banner {
  background-color: #d4edda; /* Warna hijau sukses yang lembut */
  color: #155724; /* Warna teks yang kontras */
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 25px;
  text-align: center;
  margin-bottom: 30px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
}

.results-banner h2 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1.6em;
}

.results-banner p {
  margin-bottom: 20px;
  font-size: 1.05em;
  line-height: 1.5;
}

.view-results-button {
  padding: 12px 24px;
  font-size: 1.1em;
  font-weight: bold;
  background-color: #28a745; /* Warna hijau yang lebih kuat */
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.view-results-button:hover {
  background-color: #218838;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}


</style>